/* Core & libraries */
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import Toggle from "react-toggle";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../resources/validations/validations";
import { useGuestProfileService } from "../../../services/guestProfileService";
import { PEOPLE_GUEST_ROUTE } from "../../../constants/routes.js";

/* Components */
import UCButton from "../../buttons";
import LabelDropdown from "../../label";
import { ReactComponent as UserDeleteIcon } from '../../../resources/images/user-x.svg';
import { ReactComponent as TrashRedIcon } from "../../../resources/images/trash-red-icon.svg";
import GuestProfileFormSkeleton from "../skeleton";
import DeleteActionModal from "../../modals/deleteActionModal";

/* Styles */
import styles from "../styles.js";
import { colors } from "../../../constants/colors";

const GuestProfileFormAC = ({ guestId, guestDetails }) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();
  const { createGuestProfile, updateGuestProfile, deleteGuestProfile } = useGuestProfileService();

  const navigate = useNavigate();
  const { guestProfileFormValidationObjAC } = useValidationService();
  const validationSchema = useMemo(
    () => createValidationPolicyYupSchema(guestProfileFormValidationObjAC),
    [intl.locale]
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(undefined);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.validateForm();
    }
  }, [intl.locale]);

  const submitForm = async (values, setSubmitting, resetForm) => {
    const label = selectedLabel === undefined ? guestDetails?.label : selectedLabel;
    const payload = {
      userEntityId: guestId || "",
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      emailAddress: values.emailAddress,
      ...(label && {
        label: {
          labelId: label.labelId,
        }
      })
    };

    if (guestId) {
      await updateGuestProfile(payload);
    } else {
      const response = await createGuestProfile(payload);
      if (response) {
        if (values?.addAnotherGuest) {
          resetForm();
          setSelectedLabel(null);
        } else {
          navigate(PEOPLE_GUEST_ROUTE);
        }
        window.scrollTo(0, 0);
      }
    }

    setSubmitting(false);
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    navigate(PEOPLE_GUEST_ROUTE);
    window.scrollTo(0, 0);
  };

  const handleDelete = async () => {
    const response = await deleteGuestProfile(guestId);
    if (response) {
      setShowDeleteModal(false);
      navigate(PEOPLE_GUEST_ROUTE);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <div className={classes.dividerContainer}>
        <hr className={classes.border} />
      </div>
      {(guestId && guestDetails) || !guestId ? (
        <>
          <div className={classes.details}>
            <div>
              <div className={classes.title}>
                <FormattedMessage id="guest.formTitle" />
              </div>
              <div className={classes.description}>
                <FormattedMessage id="guest.fromSubtitle" />
              </div>
            </div>
            <div className={classes.btnContainer}>
              <UCButton
                type="default"
                className={classes.buttonCancel}
                onClick={cancelUpdate}
                text={<FormattedMessage id="guest.cancel" />}
              />
              <UCButton
                type="primary"
                className={classes.button}
                onClick={handleFormSubmit}
                text={<FormattedMessage id="guest.save" />}
                spinner={{
                  name: "guestProfilePage",
                  size: 20,
                  color: colors.white,
                }}
              />
            </div>
          </div>

          <div className={classes.dividerContainer}>
            <hr className={classes.border} />
          </div>

          <div className={classes.editCompany}>
            <Formik
              innerRef={formRef}
              enableReinitialize={true}
              initialValues={{
                firstName: guestDetails?.firstName || "",
                middleName: guestDetails?.middleName || "",
                lastName: guestDetails?.lastName || "",
                emailAddress: guestDetails?.emailAddress || "",
                addAnotherGuest: false,
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                submitForm(values, setSubmitting, resetForm);
              }}
            >
              {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="guest.identityDetails" />
                          <span className={classes.requiredAsterisk}>*</span>
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="firstName" className={classes.label}>
                              <FormattedMessage id="guest.firstName" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage({ id: 'guest.firstName' })}
                              type="text"
                              name="firstName"
                              id="firstName"
                              className={errors.firstName && touched.firstName ? "input-error" : null}
                            />
                            <ErrorMessage name="firstName" component="span" className={classes.error} />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="middleName" className={classes.label}>
                              <FormattedMessage id="guest.middleName" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage({ id: 'guest.middleName' })}
                              type="text"
                              name="middleName"
                              id="middleName"
                              className={errors.middleName && touched.middleName ? "input-error" : null}
                            />
                            <ErrorMessage name="middleName" component="span" className={classes.error} />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="lastName" className={classes.label}>
                              <FormattedMessage id="guest.lastName" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage({ id: 'guest.lastName' })}
                              type="text"
                              name="lastName"
                              id="lastName"
                              className={errors.lastName && touched.lastName ? "input-error" : null}
                            />
                            <ErrorMessage name="lastName" component="span" className={classes.error} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="guest.travelerInfo" />
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <label htmlFor="emailAddress" className={classes.label}>
                              <FormattedMessage id="guest.emailAddress" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage({ id: 'guest.emailAddress' })}
                              type="text"
                              name="emailAddress"
                              id="emailAddress"
                              className={errors.emailAddress && touched.emailAddress ? "input-error" : null}
                            />
                            <ErrorMessage name="emailAddress" component="span" className={classes.error} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className={classes.location}>
                          <FormattedMessage id="guest.label" />
                        </div>
                      </div>
                      <div className="col-12 col-md-8">
                        <LabelDropdown
                          activeLabel={guestDetails?.label || selectedLabel}
                          setSelectedLabelCallback={setSelectedLabel}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <Row className={classes.formFooter}>
                    <Col xs={6}>
                      {guestId ? (
                        <UCButton
                          type="deleteLink"
                          icon={<TrashRedIcon />}
                          className={classes.deleteCompanyButton}
                          text={<FormattedMessage id="guest.deleteProfile" />}
                          onClick={() => setShowDeleteModal(true)}
                        />
                      ) : (
                        <div className={`${classes.toggle} ${classes.toggleContainer}`}>
                          <Toggle
                            icons={null}
                            onChange={(e) => setFieldValue("addAnotherGuest", e.target.checked)}
                            checked={values.addAnotherGuest}
                          />
                          <span><FormattedMessage id="guest.addAnotherGuest" /></span>
                        </div>
                      )}
                    </Col>
                    <Col xs={6}>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.buttonCancel}
                            onClick={cancelUpdate}
                            text={<FormattedMessage id="guest.cancel" />}
                          />
                          <UCButton
                            type="primary"
                            className={classes.button}
                            onClick={handleFormSubmit}
                            text={<FormattedMessage id="guest.save" />}
                            spinner={{
                              name: "guestProfilePage",
                              size: 20,
                              color: colors.white,
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              )}
            </Formik>

            {guestId && (
              <DeleteActionModal
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                setSelectedRow={() => {}}
                modalIcon={<UserDeleteIcon />}
                modalTitle={<FormattedMessage id="guest.delete.title" />}
                modalDescription={<FormattedMessage id="guest.delete.description" />}
                submitCallback={handleDelete}
              />
            )}
          </div>
        </>
      ) : (
        <GuestProfileFormSkeleton />
      )}
    </>
  );
};

export default GuestProfileFormAC;
